import { useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  approveCompany,
  bulkApproveCompanies,
  bulkRejectCompanies,
  listCompanies,
  rejectCompany,
} from '../../../masters/company/api/companyApi'
import { useMultiSelect } from '../../../../hooks/useMultiSelect'
import { useAuth } from '../../../../hooks/useAuth'
import { useApprovalSearchParams } from '../../hooks/useApprovalSearchParams'

const DEFAULT_PAGE_SIZE = 10

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function normalizeStatus(row) {
  if (Number(row?.is_archive) === 1) return 'ARCHIVED'
  const explicitStatus = String(row?.approval_status || '').toUpperCase()
  if (['PENDING', 'APPROVED', 'REJECTED'].includes(explicitStatus)) return explicitStatus
  return Number(row?.is_approved) === 1 ? 'APPROVED' : 'PENDING'
}

export function useCompanyApprovalScreen() {
  const { searchParams, updateParam, setPageParam } = useApprovalSearchParams()
  const { hasAnyPrivilege } = useAuth()
  const canDecideCompany = hasAnyPrivilege(['CompanyApprove', 'ApprovalCompanyApprove'])
  const canEditCompany = hasAnyPrivilege(['CompanyEdit', 'CompanyApprove', 'ApprovalCompanyApprove', 'ApprovalMastersEdit'])
  const page = Number(searchParams.get('page') || 1)
  const searchText = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || 'PENDING'
  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [totals, setTotals] = useState({ pending: 0, approved: 0, rejected: 0, archived: 0 })
  const [actionError, setActionError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [activeActionId, setActiveActionId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  
  const { isMultiSelectMode, selectedIds, toggleMultiSelectMode, toggleSelection, selectAll, clearSelection } = useMultiSelect()

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      include_total: true,
      search: searchText.trim(),
    }
    if (statusFilter !== 'all') {
      params.approval_status = statusFilter
      params.archive = 0
    }
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    return params
  }, [page, pageSize, searchText, statusFilter, fromDate, toDate])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listCompanies(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total_count || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.page_size || pageSize),
          hasMore: Boolean(response?.has_next),
        })
        setTotals(response?.approval_summary || { pending: 0, approved: 0, rejected: 0, archived: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load company approvals.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, refreshTick])

  const mappedRows = useMemo(() => {
    return rows
      .map((row) => ({
        id: Number(row.id),
        companyCode: row.company_code || '-',
        companyName: row.name || '-',
        gstNumber: row.gst_tax_number || '-',
        createdBy: row.created_by_name || (row.created_by ? 'User' : '-'),
        createdAt: formatDate(row.created_at),
        approvalStatus: normalizeStatus(row),
        canApprove: canDecideCompany && normalizeStatus(row) === 'PENDING',
        canEdit: canEditCompany && normalizeStatus(row) !== 'ARCHIVED',
      }))
      .filter((row) => statusFilter === 'all' || row.approvalStatus === statusFilter)
  }, [canDecideCompany, canEditCompany, rows, statusFilter])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])



  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPageParam(nextPage)
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  async function runDecision(companyId, actionType) {
    setActionError('')
    setActionMessage('')
    setActiveActionId(companyId)
    try {
      if (actionType === 'approve') {
        await approveCompany(companyId)
        setActionMessage('Company approved successfully.')
      } else {
        await rejectCompany(companyId)
        setActionMessage('Company rejected successfully.')
      }
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Company decision failed.'))
    } finally {
      setActiveActionId(null)
    }
  }

  async function runBulkDecision(actionType) {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    setActionError('')
    setActionMessage('')
    try {
      if (actionType === 'approve') {
        await bulkApproveCompanies({ ids })
        setActionMessage(`Successfully approved ${ids.length} company/ies.`)
      } else {
        await bulkRejectCompanies({ ids })
        setActionMessage(`Successfully rejected ${ids.length} company/ies.`)
      }
      clearSelection()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Bulk action failed.'))
    }
  }

  return {
    state: {
      isLoading,
      error,
      actionError,
      actionMessage,
      activeActionId,
      rows: mappedRows,
      page,
      totalPages,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalCount: meta.total,
      rangeStart,
      rangeEnd,
      searchText,
      statusFilter,
      fromDate,
      toDate,
      totals,
      isMultiSelectMode,
      selectedIds,
      canDecideCompany,
      canEditCompany,
    },
    actions: {
      setSearchText: (value) => updateParam('search', value),
      setStatusFilter: (value) => updateParam('status', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      goToPreviousPage,
      goToNextPage,
      goToPage,
      approveCompany: (companyId) => runDecision(companyId, 'approve'),
      rejectCompany: (companyId) => runDecision(companyId, 'reject'),
      toggleMultiSelectMode,
      toggleSelection,
      selectAll: () => {
        const selectable = mappedRows.filter(r => r.approvalStatus === 'PENDING' && r.canApprove).map(r => r.id)
        selectAll(selectable)
      },
      bulkApprove: () => runBulkDecision('approve'),
      bulkReject: () => runBulkDecision('reject'),
    },
  }
}



